import fileDownload from 'js-file-download';
import { makeAutoObservable } from 'mobx';

import api from '@app/api';
import { notifications } from '@shared/utils/notifications';
import { inject, injectable } from 'inversify';
import 'reflect-metadata';
import { getOsiAbonents } from '@shared/api/osi/abonents/get';
import { Abonent } from '@shared/types/osi/abonents';
import logger from 'js-logger';
import { flatComparator } from '@shared/utils/helpers/flatComparator';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { AreaTypeCodes } from '@shared/types/dictionaries';
import { downloadPdfOrZip } from '@shared/utils/download';

export interface IPartialInvoicesStore {
  selectedIds: Array<number>;
}

@injectable()
export class PartialInvoicesStore implements IPartialInvoicesStore {
  selectedIds: Array<number>;

  isLoading = false;

  // OSI-845: галочка «Отдельными квитанциями» (по умолчанию снята, в localStorage не сохраняется)
  isSeparateFiles = false;

  private _abonents: Abonent[] = [];

  constructor(@inject(OsiServiceIdentifier) private osiModule: OsiService) {
    makeAutoObservable(this);
    this.selectedIds = [];
    this.isLoading = false;
  }

  get sortedAbonents() {
    return this._abonents
      .map((abonent) => {
        // Фаза 2: не мутируем flat. Арендатор → flatDisplay (единый номер от backend),
        // остальные типы — сырой flat как раньше (makeFlat здесь не применялся). Запрос идёт по id.
        if (abonent.areaTypeCode === AreaTypeCodes.ARENDATOR) {
          return { ...abonent, flat: abonent.flatDisplay ?? abonent.flat };
        }
        return abonent;
      })
      .sort((a, b) => flatComparator(a?.flat, b?.flat));
  }

  loadAbonents = async () => {
    try {
      if (!this.osiModule.osiId) throw new Error('osiId is undefined');
      this.isLoading = true;
      this._abonents = await getOsiAbonents(this.osiModule.osiId);
    } catch (e) {
      logger.error(e);
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };

  restoreSelectedIds = (): Array<number> => {
    if (!this.osiModule.osiId) return [];

    const savedIds = localStorage.getItem(`${this.osiModule.osiId}selectedInvoices`);
    if (savedIds === null) return [];

    return JSON.parse(savedIds);
  };

  setSelectedIds = (ids: any): void => {
    this.selectedIds = ids;
    this.saveSelectedIds();
  };

  saveSelectedIds = (): void => {
    if (!this.osiModule.osiId) return;
    localStorage.setItem(`${this.osiModule.osiId}selectedInvoices`, JSON.stringify(this.selectedIds));
  };

  // OSI-845: переключение галочки «Отдельными квитанциями»
  setIsSeparateFiles = (value: boolean): void => {
    this.isSeparateFiles = value;
  };

  getInvoices = async (): Promise<void> => {
    try {
      this.isLoading = true;
      if (this.isSeparateFiles) {
        // OSI-845: отдельные файлы — сервер вернёт ZIP (или PDF, если квитанция одна);
        // формат определяем по сигнатуре ответа, имя файла — с соответствующим расширением.
        const blob = (await api.getPartialInvoices(this.selectedIds, true)) as unknown as Blob;
        await downloadPdfOrZip(blob, `Отдельные квитанции_${new Date().toISOString()}`);
      } else {
        const data = (await api.getPartialInvoices(this.selectedIds)) as unknown as ArrayBuffer;
        fileDownload(data, `Отдельные квитанции_${new Date().toISOString()}.pdf`);
      }
      notifications.success();
      this.saveSelectedIds();
    } catch (error: any) {
      logger.error(error);
      if (error?.isAxiosError) {
        let errorObj = error.response.data;
        let message = '';
        if (
          error.request.responseType === 'blob' &&
          error.response.data instanceof Blob &&
          error.response.data.type &&
          error.response.data.type.toLowerCase().indexOf('json') !== -1
        ) {
          errorObj = JSON.parse(await error.response.data.text());
          message = errorObj?.message;
        }
        notifications.error(`Ошибка при получении квитанции. ${message}`);
        return;
      }
      notifications.error();
    } finally {
      this.isLoading = false;
    }
  };
}
